import moment from 'moment';

import { rangeYearsAndMonthsFrom2015toCurrentDate } from '../../helpers/array';
import {
    CLEAR,
    BILLING_LIST_REQUEST,
    BILLING_LIST_SUCCESSFUL,
    BILLING_LIST_FAILED,
    BILLING_SET_FILTER,
    BILLING_SET_SCOPE,
    BILLING_SET_RESOLVED_PROFILE,
    BILLING_FETCH_CANCELLED,
    GET_ONGOING_TRIP_RECEIPTS,
    SET_ONGOING_TRIP_RECEIPTS_LOADING,
    SET_ONGOING_TRIP_RECEIPTS,
} from './types';

const currentDate = moment();

// Per-scope fetching flags so each tab can show its own loading state without
// being affected by an in-flight fetch for the other tab.
const initFetching = { trips: false, others: false };

const initState = {
    fetching: initFetching,
    list: null,
    year: currentDate.year(),
    month: currentDate.month(),
    profile: '',
    profiles: [],
    listFilter: rangeYearsAndMonthsFrom2015toCurrentDate(),
    // Per-tab filter state, so trips and other charges keep independent memory
    activeScope: null,
    tabMemory: {},
    // Last filter values the saga used to fetch each scope's data; used by the
    // Filter to skip a refetch when restoring a tab whose data is still valid.
    lastFetchedFilter: {},
    ongoingTripsReceipts: {
        loading: false,
        data: {},
    },
};

const reducer = (state = initState, action) => {
    switch (action.type) {
        case CLEAR:
            return {
                ...initState,
            };

        case BILLING_LIST_REQUEST:
            // Full load refreshes both scopes.
            return {
                ...state,
                fetching: { trips: true, others: true },
            };

        case BILLING_LIST_SUCCESSFUL: {
            const { trips: dataTrips, others: dataOthers, old, filterUsed, scopesFetched } = action.payload;
            const currentList = state.list || { trips: [], others: [], old: [] };

            let { trips } = currentList;
            if (dataTrips !== undefined) {
                const data = dataTrips.Billing ? dataTrips.Billing : dataTrips;
                trips = data.map(item => {
                    const date = moment(item.trip.tripStartDate);
                    return {
                        ...item,
                        year: date.year(),
                        month: date.month(),
                    };
                });
                trips.sort((a, b) => {
                    if (a.trip.tripStartDate < b.trip.tripStartDate) {
                        return 1;
                    }
                    if (a.trip.tripStartDate > b.trip.tripStartDate) {
                        return -1;
                    }
                    return 0;
                });
            }

            let { others } = currentList;
            if (dataOthers !== undefined) {
                others = dataOthers.map(item => {
                    const date = moment(item.invoice.billingDate);
                    return {
                        ...item,
                        year: date.year(),
                        month: date.month(),
                    };
                });
                others.sort((a, b) => {
                    if (a.invoice.billingDate < b.invoice.billingDate) {
                        return 1;
                    }
                    if (a.invoice.billingDate > b.invoice.billingDate) {
                        return -1;
                    }
                    return 0;
                });
            }

            const nextLastFetched = { ...state.lastFetchedFilter };
            const nextFetching = { ...state.fetching };
            if (scopesFetched) {
                if (scopesFetched.trips) {
                    nextFetching.trips = false;
                    if (filterUsed) nextLastFetched.trips = filterUsed;
                }
                if (scopesFetched.others) {
                    nextFetching.others = false;
                    if (filterUsed) nextLastFetched.others = filterUsed;
                }
            } else {
                nextFetching.trips = false;
                nextFetching.others = false;
            }

            return {
                ...state,
                fetching: nextFetching,
                list: {
                    trips,
                    others,
                    old: old !== undefined ? old : currentList.old,
                },
                lastFetchedFilter: nextLastFetched,
            };
        }

        case BILLING_LIST_FAILED:
            // Clear all in-flight markers; we don't know which scope failed.
            return {
                ...state,
                fetching: { trips: false, others: false },
            };

        case BILLING_SET_FILTER: {
            const { year, month, profile, scope } = action.payload;
            // A scoped filter change only fetches that scope; without a scope
            // (legacy callers) treat it as a full refresh.
            const nextFetching = scope ? { ...state.fetching, [scope]: true } : { trips: true, others: true };
            return {
                ...state,
                year,
                month,
                profile,
                fetching: nextFetching,
                ...(scope
                    ? {
                          activeScope: scope,
                          tabMemory: {
                              ...state.tabMemory,
                              [scope]: { year, month, profile },
                          },
                      }
                    : {}),
            };
        }

        case BILLING_SET_SCOPE: {
            const { scope, filter } = action.payload;
            const nextFilter = filter || { year: state.year, month: state.month, profile: state.profile };
            return {
                ...state,
                ...(filter ? { year: filter.year, month: filter.month, profile: filter.profile } : {}),
                activeScope: scope,
                tabMemory: {
                    ...state.tabMemory,
                    [scope]: nextFilter,
                },
            };
        }

        case BILLING_SET_RESOLVED_PROFILE: {
            const { profile } = action.payload;
            const { activeScope, tabMemory } = state;
            const scopeMemory = activeScope && tabMemory[activeScope];
            return {
                ...state,
                profile,
                ...(scopeMemory
                    ? {
                          tabMemory: {
                              ...tabMemory,
                              [activeScope]: { ...scopeMemory, profile },
                          },
                      }
                    : {}),
            };
        }

        case BILLING_FETCH_CANCELLED: {
            const { scopesFetched } = action.payload;
            const nextFetching = { ...state.fetching };
            if (scopesFetched.trips) nextFetching.trips = false;
            if (scopesFetched.others) nextFetching.others = false;
            return {
                ...state,
                fetching: nextFetching,
            };
        }

        case GET_ONGOING_TRIP_RECEIPTS:
            return {
                ...state,
                ongoingTripsReceipts: {
                    ...state.ongoingTripsReceipts,
                    loading: true,
                },
            };

        case SET_ONGOING_TRIP_RECEIPTS_LOADING:
            return {
                ...state,
                ongoingTripsReceipts: {
                    ...state.ongoingTripsReceipts,
                    loading: action.payload,
                },
            };
        case SET_ONGOING_TRIP_RECEIPTS:
            return {
                ...state,
                ongoingTripsReceipts: {
                    ...state.ongoingTripsReceipts,
                    loading: false,
                    data: action.payload,
                },
            };
        default:
            return state;
    }
};

export default reducer;
